import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { logger } from "@utilifeed/core";
import { useDataApi, useWeatherApi } from "@core/apis";
import {
  getAverageSensorDataPerSubstationOptions,
  getFlexStationParametersOptions,
  useWeatherCoordinates,
  type Sensor,
  type SensorDataPerSubstation,
} from "@core/apis/shared";
import { getWeatherOptions } from "@core/apis/Weather";

import { AVERAGE_SENSOR_UID } from "../IndoorClimate/SensorOverview";
import { sanitizeSensorDataArray } from "../sensorUtils";
import type { DateRange } from "../shared/types";
import { useSensorsWithData } from "./useSensorWithData";

/** Sanitize all sensors except the average sensor. */
function sanitizeSensors(sensors: Sensor[] | undefined): Sensor[] {
  if (!sensors) return [];
  return sensors.map((sensor) =>
    sensor.uid === AVERAGE_SENSOR_UID
      ? sensor
      : { ...sensor, data: sanitizeSensorDataArray(sensor.data ?? []) }
  );
}

/**
 * Data for the SensorOverview (KPI cards + sensor grid).
 *
 * @param substationId - The substation identifier
 * @param options.flexStationUid - FlexStation UID for target temperature lookup.
 *   PropAI: from useGetFlexStations(). Platform: from station.subUid prop.
 * @param options.networkUid - Platform requires this for flex station parameter queries.
 */
export function useIndoorClimateOverviewData(
  substationId: string,
  options?: { flexStationUid?: string; networkUid?: string }
) {
  const dataApi = useDataApi();

  const overviewStart = useMemo(() => DateTime.now().minus({ hours: 24 }).startOf("hour"), []);
  const overviewEnd = useMemo(() => DateTime.now().plus({ hour: 1 }).startOf("hour"), []);

  const { sensorsWithData, fetchingSensors } = useSensorsWithData(substationId, {
    startDate: overviewStart,
    endDate: overviewEnd,
  });

  // Target temperature from flex station parameters.
  // Intentionally not included in isLoading — a failure here should not block the sensor grid.
  const { data: stationParameters } = useQuery({
    ...getFlexStationParametersOptions(
      dataApi,
      options?.flexStationUid ?? null,
      substationId,
      options?.networkUid
    ),
    enabled: !!options?.flexStationUid && !!dataApi,
  });
  const targetTemperature = stationParameters?.indoor_temp?.min ?? null;

  const sanitizedSensors = useMemo(() => sanitizeSensors(sensorsWithData), [sensorsWithData]);

  const individualSensors = useMemo(
    () => sanitizedSensors.filter((s) => s.uid !== AVERAGE_SENSOR_UID),
    [sanitizedSensors]
  );

  const fullSensorList = useMemo(
    () =>
      individualSensors.map((sensor) => ({
        ...sensor,
        street_address: sensor.street_address || "",
        apartment: sensor.apartment || "",
      })),
    [individualSensors]
  );

  const avgSensor = useMemo(
    () => sanitizedSensors.find((s) => s.uid === AVERAGE_SENSOR_UID) ?? null,
    [sanitizedSensors]
  );

  const isLoading = fetchingSensors;
  const isEmpty = !isLoading && individualSensors.length === 0;

  return {
    data: { individualSensors, fullSensorList, avgSensor, targetTemperature },
    isLoading,
    isEmpty,
  };
}

/**
 * Data for the indoor climate graph (time series chart + signature diagram).
 *
 * @param substationId - Substation identifier
 * @param dateRange - Date range
 * @param options.networkUid - Platform requires this for weather coordinate lookup.
 */
export function useIndoorClimateGraphData(
  substationId: string,
  dateRange: DateRange,
  options?: { networkUid?: string }
) {
  const dataApi = useDataApi();
  const weatherApi = useWeatherApi();

  const { sensorsWithData, fetchingAverageSensorData, fetchingSensors } = useSensorsWithData(
    substationId,
    dateRange as { startDate: DateTime; endDate: DateTime }
  );

  // Weather coordinates (shared hook)
  const { data: weatherCoordinates } = useWeatherCoordinates(substationId, options?.networkUid);

  // Outdoor temperature
  // Tries sensor first, then falls back to weather API
  const { data: outdoorSensorData, isLoading: loadingOutdoorSensor } = useQuery(
    getAverageSensorDataPerSubstationOptions(
      dataApi,
      substationId,
      "outdoor_temperature",
      dateRange.startDate,
      dateRange.endDate
    )
  );

  const { data: outdoorWeatherData } = useQuery(
    getWeatherOptions(
      weatherApi,
      {
        coordinates: weatherCoordinates,
        startDate: dateRange.startDate?.toISO() ?? null,
        endDate: dateRange.endDate?.toISO() ?? null,
      },
      !loadingOutdoorSensor && !outdoorSensorData?.length
    )
  );

  const outdoorSeries = useMemo((): { x: number; y: number }[] => {
    if (outdoorSensorData?.length) {
      return outdoorSensorData
        .filter(
          (
            item: SensorDataPerSubstation
          ): item is SensorDataPerSubstation & { values: { outdoor_temperature: number } } =>
            item.values?.outdoor_temperature != null
        )
        .map((item) => ({
          x: DateTime.fromISO(item.timestamp).toMillis(),
          y: item.values.outdoor_temperature,
        }));
    }
    if (outdoorWeatherData) {
      logger.warn("No outdoor sensor data found, using weather data");
      return outdoorWeatherData.map((item) => ({
        x: DateTime.fromISO(item.datetime).toMillis(),
        y: item.value,
      }));
    }
    return [];
  }, [outdoorSensorData, outdoorWeatherData]);

  const sanitizedSensors = useMemo(() => sanitizeSensors(sensorsWithData), [sensorsWithData]);

  const isLoading = fetchingAverageSensorData || loadingOutdoorSensor || fetchingSensors;
  const isEmpty = !isLoading && sanitizedSensors.length === 0 && outdoorSeries.length === 0;

  return {
    data: { sanitizedSensors, outdoorSeries, weatherCoordinates },
    isLoading,
    isEmpty,
  };
}
